import { z } from 'zod';
import {
  common,
  exploration,
  conversation,
  metric,
  model,
  pipeline,
  queryPipeline,
} from '@gosupersimple/types';

import { Json, Literal, timeRange } from '../lib/types';
import { isNumericField } from './model/utils';

export const explorationTypes = ['exploration', 'model', 'ai'] as const;
export type ExplorationType = (typeof explorationTypes)[number] | null;

export interface Exploration {
  schemaVersion?: exploration.Exploration['schemaVersion'];
  explorationId: string;
  name: string;
  description?: string;
  labels: Record<string, string>;
  parameters: { key: string; modelId: string }[];
  view: {
    cells: Cell[];
    canvas?: CanvasState;
    options?: ExplorationViewOptions;
    tabs?: {
      groups: { tabs: ExplorationTab[] }[];
    };
  };
  options?: {
    /**
     * Stores the persisted exploration ID if available. Used to link back to the persisted state.
     */
    explorationSourceId?: string;
    previousStateId?: string;
    explorationForModelId?: string;
    detailExplorationForModelId?: string;
  };
  createdAt?: string;
  createdBy?: { email: string };
  updatedAt?: string;
  updatedBy?: { email: string };
}

const explorationLayout = z.enum(['notebook', 'canvas']);
export type ExplorationLayout = z.infer<typeof explorationLayout>;

export const explorationViewOptions = z
  .object({
    layout: explorationLayout,
  })
  .partial();

export type ExplorationViewOptions = z.infer<typeof explorationViewOptions>;

export interface CanvasEdge {
  id: string;
  source: string;
  target: string;
}

export interface CanvasNode {
  id: string;
  position: { x: number; y: number };
  measured: { width: number; height: number };
  zIndex: number;
  type?: string;
  precomputed?: boolean;
}

export interface CanvasState {
  edges: CanvasEdge[];
  nodes: CanvasNode[];
}

export interface ChatExploration {
  id: string;
  role: 'assistant';
  type: 'exploration';
  exploration: Exploration;
  jobId?: string;
}

export interface ChatUserQuestion {
  id: string;
  role: 'user';
  type: 'initial_user_prompt' | 'followup_question';
  sources: DataSource[];
  research?: boolean;
  message: string;
  jobId?: string;
}

export type DataSource = conversation.DataSource;

export type ChatUserMessage = conversation.ClarifyingAnswerMessage | ChatUserQuestion;

export interface ChatAssistantMessage {
  id: string;
  message: string;
  role: 'assistant';
  type: 'clarifying_question';
  jobId?: string;
}

export interface ChatProgressMessage {
  id: string;
  message: string;
  role: 'assistant';
  type: 'progress';
  jobId?: string;
}

export type ChatMessage = ChatUserMessage | ChatAssistantMessage | ChatProgressMessage;

export enum ChatSource {
  Database = 'dwh',
  //Account = 'account_context',
}
export type ChatMode = 'research' | 'normal';

export type ConversationJobProgress = {
  jobProgressId: string;
  jobId: string;
  status: 'idle' | 'running' | 'error' | 'completed' | 'cancelled';
  statusMessage?: string;
  createdAt: string;
};

export type ConversationJob = {
  jobId: string;
  parentJobId?: string;
  progress: ConversationJobProgress[];
  channel?: string;
  createdAt: string;
};

export type Conversation = {
  conversationId: string;
  messages: (ChatMessage | ChatExploration)[];
  jobs: ConversationJob[];
};

export type SortItem = common.Sorting;

export type Sort = SortItem[];

export const cellViewOptions = exploration.cellViewOptions;
export type CellViewOptions = exploration.CellViewOptions;

export type SingleColorRule = exploration.SingleColorRule;
export type ColorScaleRule = exploration.ColorScaleRule;
export type TextFormat = exploration.TextFormat;
export type FormattingRule = SingleColorRule | ColorScaleRule;
export type ConditionalFormattingOperator = exploration.ConditionalFormattingOperator;
export type ColorScalePointType = exploration.ColorScalePointType;
export type ColorScalePoint = exploration.ColorScalePoint;
export type ColorScaleMidPoint = exploration.ColorScaleMidPoint;

export type RecordsCell = {
  id: string;
  kind: 'records';
  title?: string | null;
  excludeProperties?: string[] | null;
  pipeline: Pipeline;
  visualisations?: Visualisation[];
  sort?: Sort;
  viewOptions?: CellViewOptions;
  conversationId?: string;
};

export type FunnelCell = {
  id: string;
  kind: 'funnel';
  title?: string | null;
  pipeline: Pipeline;
  viewOptions?: CellViewOptions;
  conversationId?: string;
};

export type CohortCell = {
  id: string;
  kind: 'cohort';
  title?: string | null;
  pipeline: Pipeline;
  viewOptions?: CellViewOptions;
  conversationId?: string;
};

export type SqlCell = {
  id: string;
  kind: 'sql';
  title?: string | null;
  pipeline: Pipeline;
  visualisations?: Visualisation[];
  viewOptions?: CellViewOptions;
  conversationId?: string;
};

export type PythonCell = {
  id: string;
  kind: 'python';
  title?: string | null;
  pipeline: Pipeline;
  viewOptions?: CellViewOptions;
  conversationId?: string;
};

export type InvalidCell = {
  id: string;
  title?: string | null;
  kind: 'invalid';
  message: string;
  viewOptions?: CellViewOptions;
  cell: Exclude<Cell, InvalidCell>;
};

export type VariableCell = {
  id: string;
  kind: 'variable';
  definition: VariableDefinition;
  viewOptions?: CellViewOptions;
  conversationId?: string;
};

export type TextCell = {
  id: string;
  kind: 'text';
  title?: string | null;
  content?: string | null;
  viewOptions?: CellViewOptions;
  conversationId?: string;
};

export type ChatCell = {
  id: string;
  kind: 'chat';
  conversationId: string;
  messages: ChatMessage[];
  viewOptions?: CellViewOptions;
};

export type ConversationCell = exploration.ConversationCell;

export type ConversationPart = Cell & {
  conversationId: string;
};

export type Cell =
  | RecordsCell
  | FunnelCell
  | VariableCell
  | CohortCell
  | SqlCell
  | PythonCell
  | TextCell
  | InvalidCell
  | ChatCell
  | ConversationCell;

export type RecordsLikeCell = RecordsCell | SqlCell;

export type CopiableCell =
  | RecordsCell
  | FunnelCell
  | CohortCell
  | SqlCell
  | PythonCell
  | TextCell
  | VariableCell;
export type CellWithPipeline = Extract<Cell, { pipeline: Pipeline }>;

export type ExplorationTab = exploration.ExplorationTab;

export const timeRangeParameter = timeRange;

export const dateRangeParameter = z.object({
  range: timeRangeParameter,
  // start/end are optional for dynamic ranges (e.g., '7d', 'prevmonth') — they're recalculated
  // from "now" at query time by parseParameterValue. Only required for range: 'custom'.
  start: z.string().optional(),
  end: z.string().optional(),
});

export type DateRangeParameter = z.infer<typeof dateRangeParameter>;

// After parseParameterValue resolves dynamic ranges, start/end are always present
export type ResolvedDateRangeParameter = DateRangeParameter &
  Required<Pick<DateRangeParameter, 'start' | 'end'>>;

export const stringParameter = z.string();
export const numberParameter = z.number();
export const booleanParameter = z.boolean();
export const arrayParameter = z.array(z.string().or(z.number()));
export const dateParameter = z.string();
export const timeIntervalParameter = common.timeInterval;

export const explorationParameter = stringParameter
  .or(numberParameter)
  .or(stringParameter)
  .or(arrayParameter)
  .or(booleanParameter)
  .or(dateRangeParameter)
  // A null parameter represents a SQL NULL record value (e.g. a nullable primary-key column in a
  // detail-view link), which must be preserved so the lookup can match NULL rows.
  .nullable();

export const explorationParameters = z.record(z.string(), explorationParameter);

export type ExplorationParameter = z.infer<typeof explorationParameter>;

export type ExplorationParameters = z.infer<typeof explorationParameters>;

const stringVariableDefinition = z.object({
  kind: z.literal('string'),
  key: z.string(),
  defaultValue: stringParameter,
});

const numberVariableDefinition = z.object({
  kind: z.literal('number'),
  key: z.string(),
  defaultValue: numberParameter,
});

const queryOptions = z
  .object({
    pipelineId: z.string(),
    field: z.string(),
    modelId: z.string().optional(),
    propertyKey: z.string().optional(),
  })
  .optional()
  .default({ pipelineId: '', field: '' });

const enumSource = z
  .literal('static')
  .or(z.literal('query'))
  .or(z.literal('model'))
  .optional()
  .default('static');

const enumVariableDefinition = z.object({
  kind: z.literal('enum'),
  key: z.string(),
  source: enumSource,
  options: z.array(z.object({ value: z.string().or(z.number()) })),
  queryOptions,
  defaultValue: stringParameter.or(numberParameter),
});

const multiselectVariableDefinition = z.object({
  kind: z.literal('multiselect'),
  key: z.string(),
  source: enumSource,
  options: z.array(z.object({ value: z.string().or(z.number()) })),
  queryOptions,
  defaultValue: arrayParameter,
});

export type EnumVariableDefinition = z.infer<typeof enumVariableDefinition>;
export type MultiselectVariableDefinition = z.infer<typeof multiselectVariableDefinition>;

const booleanVariableDefinition = z.object({
  kind: z.literal('boolean'),
  key: z.string(),
  defaultValue: booleanParameter,
});

const dateVariableDefinition = z.object({
  kind: z.literal('date'),
  key: z.string(),
  defaultValue: dateParameter,
});

const dynamicVariableDefinition = z
  .object({
    kind: z.literal('dynamic'),
    key: z.string(),
    pipelineId: z.string(),
    field: z.string(),
    defaultValue: stringParameter,
  })
  .transform(
    (input): EnumVariableDefinition => ({
      kind: 'enum',
      key: input.key,
      source: 'query',
      options: [],
      queryOptions: {
        pipelineId: input.pipelineId,
        field: input.field,
        modelId: '',
        propertyKey: '',
      },
      defaultValue: input.defaultValue,
    }),
  );
export type DynamicVariableDefinition = z.infer<typeof dynamicVariableDefinition>;

// Custom preset option for date range variables (e.g., '21d', '45d')
const customPresetOption = z.object({
  value: z.string(), // Dynamic shorthand like '21d', '45d', '3w'
  label: z.string().optional(), // Auto-generated if omitted
});

export type CustomPresetOption = z.infer<typeof customPresetOption>;

// Configuration for date range presets
const dateRangePresetConfig = z.object({
  enabledPresets: z.array(z.string()).optional(), // Built-in presets to show (if undefined, show all)
  customPresets: z.array(customPresetOption).optional(), // User-defined presets
  allowCustomRange: z.boolean().optional(), // Show calendar picker (default: true)
});

export type DateRangePresetConfig = z.infer<typeof dateRangePresetConfig>;

const dateRangeVariableDefinition = z.object({
  kind: z.literal('date_range'),
  key: z.string(),
  defaultValue: dateRangeParameter,
  presetConfig: dateRangePresetConfig.optional(), // Preset customization config
});

export type DateRangeVariableDefinition = z.infer<typeof dateRangeVariableDefinition>;

const timeIntervalVariableDefinition = z.object({
  kind: z.literal('time_interval'),
  key: z.string(),
  defaultValue: timeIntervalParameter,
});

export const variableDefinition = z.union([
  stringVariableDefinition,
  numberVariableDefinition,
  enumVariableDefinition,
  multiselectVariableDefinition,
  booleanVariableDefinition,
  dateVariableDefinition,
  dynamicVariableDefinition,
  dateRangeVariableDefinition,
  timeIntervalVariableDefinition,
]);

export type VariableDefinition = z.infer<typeof variableDefinition>;

export type BasePipeline = {
  pipelineId: string;
  baseModelId: string;
  operations: PipelineOperation[];
};

export type PipelineWithParent = {
  pipelineId: string;
  parentId: string;
  operations: PipelineOperation[];
};

export type DereferencedPipeline = {
  baseModelId: string;
  operations: DereferencedPipelineOperation[];
};

export type Pipeline = BasePipeline | PipelineWithParent;

export type PipelineOperation = (
  | DeriveFieldOperation
  | FilterOperation
  | GroupAggregateOperation
  | AddRelatedColumnOperation
  | RelationAggregateOperation
  | SwitchToRelationOperation
  | JoinPipelineOperation
  | HistogramOperation
  | FunnelOperation
  | CohortOperation
  | SqlOperation
  | PythonOperation
  | InvalidOperation
) & {
  disabled?: boolean;
};

export type DereferencedPipelineOperation = (
  | DeriveFieldOperation
  | FilterOperation
  | GroupAggregateOperation
  | DereferencedAddRelatedColumnOperation
  | DereferencedRelationAggregateOperation
  | SwitchToRelationOperation
  | DereferencedJoinPipelineOperation
  | HistogramOperation
  | DereferencedFunnelOperation
  | DereferencedCohortOperation
  | SqlOperation
  | PythonOperation
  | queryPipeline.InvalidOperation
  | queryPipeline.NoOpOperation
) & {
  disabled?: boolean;
};

export type Property = z.infer<typeof common.property>;

export const timeAggregationPeriodZod = common.timeInterval;
export type TimeAggregationPeriod = z.infer<typeof timeAggregationPeriodZod>;

export const groupingZod = common.grouping;
export type Grouping = common.Grouping;

const _aggregationTypeZod = common.aggregationType;
export type AggregationType = z.infer<typeof _aggregationTypeZod>;

export const aggregationZod = common.aggregation;
export type Aggregation = common.Aggregation;

export type KeyedAggregation = Extract<Aggregation, { key: string }>;
export type ExpressionAggregation = Extract<Aggregation, { value: common.ExpressionValue }>;
export type MetricAggregation = Extract<Aggregation, { metricId: string }>;
export type SortedAggregation = Extract<Aggregation, { sort: common.Sorting[] }>;
export type FilteringAggregation = common.FilteringAggregation;

export type AggregationExtendedType = AggregationType | 'earliest' | 'latest';

export type FilterOperator = common.FilterCondition['operator'];
export type FilterValue = common.FilterValue;
export type FilterCondition = common.FilterCondition;
export type CompositeFilterCondition = common.CompositeFilterCondition;
export type FunnelStep = pipeline.FunnelStep;

export type DeriveFieldOperation = pipeline.DeriveFieldOperation;
export type FilterOperation = pipeline.FilterOperation;
export type GroupAggregateOperation = pipeline.GroupAggregateOperation;
export type AddRelatedColumnOperation = pipeline.AddRelatedColumnOperation;
export type RelationAggregateOperation = pipeline.RelationAggregateOperation;
export type SwitchToRelationOperation = pipeline.SwitchToRelationOperation;
export type JoinPipelineOperation = pipeline.JoinPipelineOperation;
export type JoinPipelineStep = pipeline.JoinPipelineStep;
type HistogramOperation = pipeline.HistogramOperation;
export type FunnelOperation = pipeline.FunnelOperation;
export type CohortOperation = pipeline.CohortOperation;
export type SqlOperation = pipeline.SQLOperation;
export type PythonOperation = pipeline.PythonOperation;
export type InvalidOperation = pipeline.InvalidOperation;

type DereferencedAddRelatedColumnOperation = queryPipeline.AddRelatedColumnOperation;
type DereferencedJoinPipelineOperation = queryPipeline.JoinPipelineOperation;
type DereferencedRelationAggregateOperation = queryPipeline.RelationAggregateOperation;
export type DereferencedFunnelOperation = queryPipeline.FunnelOperation;
type DereferencedCohortOperation = queryPipeline.CohortOperation;

export const bigNumberOptions = exploration.bigNumberOptions;
export type BigNumberOptions = exploration.BigNumberOptions;

export const bigNumberWithComparisonOptions = exploration.bigNumberWithComparisonOptions;
export type BigNumberWithComparisonOptions = exploration.BigNumberWithComparisonOptions;

export const chartTypeSchema = exploration.chartTypeSchema;
export type ChartType = exploration.ChartType;

export const chartAxisSchema = exploration.chartAxisSchema;
export type ChartAxis = exploration.ChartAxis;

export const visualisationOrientationSchema = exploration.visualisationOrientationSchema;
export type VisualisationOrientation = exploration.VisualisationOrientation;

export const seriesItemSchema = exploration.seriesItemSchema;
export type SeriesItemViewOptions = exploration.SeriesItemViewOptions;

export const seriesSchema = exploration.seriesSchema;
export type SeriesViewOptions = exploration.SeriesViewOptions;

export const visualisationViewOptions = exploration.visualisationViewOptions;
export type VisualisationViewOptions = exploration.VisualisationViewOptions;

export const aggregatedVisualisation = exploration.aggregatedVisualisation;
export type AggregatedVisualisation = exploration.AggregatedVisualisation;

export const isAggregatedVisualisation = (
  visualisation: Visualisation,
): visualisation is AggregatedVisualisation => 'aggregation' in visualisation;

export const simpleVisualisationZod = exploration.simpleVisualisation;
export type SimpleVisualisation = exploration.SimpleVisualisation;

export const isSimpleVisualisation = (
  visualisation: Visualisation,
): visualisation is SimpleVisualisation =>
  !('aggregation' in visualisation) && visualisation.mainAxisKey !== undefined;

export const visualisationZod = exploration.visualisation;
export type Visualisation = exploration.Visualisation;

export type HistogramVisualisation = AggregatedVisualisation & {
  mainAxisKey: string;
};

export const isHistogramVisualisation = (
  visualisation: Visualisation,
  fields: Field[],
): visualisation is HistogramVisualisation => {
  const mainAxisField = fields.find((field) => field.key === visualisation.mainAxisKey);
  return (
    mainAxisField !== undefined &&
    isNumericField(mainAxisField) &&
    isAggregatedVisualisation(visualisation) &&
    visualisation.aggregation.groups.length === 1 &&
    visualisation.aggregation.aggregations.length === 1 &&
    visualisation.aggregation.aggregations.at(0)?.type === 'count'
  );
};

export const modelKinds = ['Account', 'User', 'Event', 'Entity'] as const;
export type ModelKind = (typeof modelKinds)[number];

export type ModelProperty = {
  key: string;
  name: string;
  type: model.PropertyType;
  format?: common.PropertyValueFormat;
  description?: string;
};

export type Model = {
  modelId: string;
  name: string;
  description?: string;
  primaryKey: string[];
  properties: ModelProperty[];
  relations: Relation[];
  labels: Record<string, string>;
  semantics?:
    | {
        kind: ModelKind;
        properties: {
          createdAt: string;
        };
      }
    | Record<string, never>;
};

export type JoinStrategyDirect = {
  joinKeyOnBase: string;
  joinKeyOnRelated: string;
  through?: {
    modelId: string;
    joinKeyToBase?: string;
    joinKeyToRelated?: string;
  };
};

export type JoinStrategySteps = {
  steps: { relationKey: string }[];
};

export type JoinStrategySQL = { sql: string };

export type JoinStrategy = JoinStrategyDirect | JoinStrategySteps | JoinStrategySQL;

export type Relation = {
  key: string;
  modelId: string;
  type: string;
  name: string;
  description?: string;
  joinStrategy?: JoinStrategy;
};

export type RelationWithBase = Relation & { baseModelId: string };

export interface Point {
  value: number;
  date: string;
}

export interface Metric {
  metricId: string;
  name: string;
  description?: string;
  definition: {
    modelId: string;
    operations?: PipelineOperation[]; // TODO: fetched metrics contain queryPipeline operations
    aggregation: metric.Metric['definition']['aggregation'];
  };
}

interface EnumOption {
  label: string;
  value: string;
}

export type EnumOptions = EnumOption[];

export interface FieldRelation {
  key: string;
  modelId: string;
  name: string;
  baseModelId?: string;
}

export interface Field {
  key: string;
  name: string;
  type: model.PropertyType | null;
  arrayItem?: { type: model.PropertyType | null };
  pk?: boolean;
  relation?: FieldRelation;
  model?: { modelId: string; name: string; propertyKey: string };
  precision?: TimeAggregationPeriod;
  format?: common.PropertyValueFormat;
  description?: string;
  aggregationType?: AggregationType;
}

export type Fields = Field[];

export type QueryVariables = common.QueryVariables;

export type PipelineStateRelation = Relation & { baseModelId: string };

export interface PipelineState<F = Fields> {
  model: Model;
  relations: PipelineStateRelation[];
  fields: F;
  pipelineId?: string;
  grouping: { key: string }[];
}

const numberRange = z.object({
  start: z.number(),
  end: z.number(),
});

export type NumberRange = z.infer<typeof numberRange>;
export const isNumberRange = (value: unknown): value is NumberRange => {
  return numberRange.safeParse(value).success;
};

export type DataTableRecord = Record<string, Json>;

export type DataTableRow = DataTableRecord & {
  $leaves?: number;
  $children?: DataTableRow[];
};

export type DataTableProperty = Field & {
  buildLink?: (record: DataTableRecord) => string;
  lineage?: {
    kind: 'pivot';
    valueKey: string;
    columnValue: Literal;
    columnKey: string;
  };
};

export const entityPropertyValueToLiteral = (value: common.EntityPropertyValue | null): Literal => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return String(value);
};
